import * as React from 'react';
import { useTranslation } from 'react-i18next';
import classNames from 'classnames';
import { observer } from 'mobx-react';

import { Button, Theme } from '@canvas-logic/clp-shared-ui';

import { Basic3dLayoutParams } from '@/ui-kit';
import { UiBuilder } from '@/UiBuilder/UiBuilder';

import { getCanvasLogicBrandingLink, isCanvasLogicBrandingEnabled, isWebArEnabled } from '../../domain/UiSpecification';
import NotificationContainer from '../Notification/NotificationContainer';
import { CanvasLogicBranding } from '../CanvasLogicBranding/CanvasLogicBranding';
import { useBootstrapper } from '../../contexts/BootstrapperContext';
import SheetRouter from '../SheetRouter/SheetRouter';
import { useSheetRouter } from '../SheetRouter/SheetRouterContext';
import { useConfiguratorRoute } from '@/hooks/useConfiguratorRoute';
import { embeddingService } from '@/ui-kit/services/embedding/EmbeddingService';
import { RequestQuoteButton } from '@/ui-kit/components/RequestQuote/RequestQuoteButton';

import { useEffect, useState } from 'react';
import Actions from '@/ui-kit/components/Actions/Actions';
import { PortalProvider } from '@/ui-kit/components/PortalContext';

function Basic3dLayout(props: Basic3dLayoutParams) {
  const { t: tTranslation } = useTranslation('translation');
  const { hideConfigurationControls, background, configuratorMode, hide3d } = useConfiguratorRoute();
  const { bootstrapper, canvasRef } = useBootstrapper();
  const { openMenu } = useSheetRouter();
  const [fullscreen, setFullscreen] = useState(false);
  const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);
  const configuratorUI = bootstrapper?.configuratorUI;
  const hiddenControls = configuratorUI?.layout?.controlsHidden || hideConfigurationControls;

  const showPrice = bootstrapper?.hasPrice;
  const contactUsService = bootstrapper?.contactUsService;
  const showRequestQuote = !fullscreen && !showPrice && bootstrapper?.isInCatalog && contactUsService?.isEnabled();

  useEffect(() => {
    return embeddingService.subscribeOnAttributeValueChange((event) => {
      const { componentKey, attributeKey, attributeValue } = event.payload;
      const sdk = bootstrapper?.sdk;
      if (sdk?.composite) {
        sdk.api.setAttributeValue(componentKey, attributeKey, attributeValue).then(() => {
          bootstrapper?.handleProductChanges();
        });
      }
    }, []);
  }, [bootstrapper]);

  useEffect(() => {
    if (configuratorMode === 'preview') {
      document.getElementById('root')?.style.setProperty('--color-background', 'transparent');
    }
  }, [configuratorMode]);

  return (
    <PortalProvider portalElement={portalContainer}>
      <main
        className={classNames('root', {
          'root--no3d': hide3d,
          ['basic-3d-layout--controls-hidden']: hiddenControls,
        })}
      >
        {configuratorUI && <Theme path={configuratorUI.theme} />}
        <NotificationContainer service={bootstrapper?.notificationService} />
        {!hide3d && (
          <div
            className={classNames(
              'viewport__container',
              configuratorMode === 'preview' && 'viewport__container--preview-mode',
            )}
          >
            <div className={'viewport-frame'} ref={setPortalContainer}>
              <div
                id="viewer"
                className={classNames('viewport', configuratorMode === 'preview' && 'viewport--preview-mode')}
              >
                <canvas
                  ref={canvasRef}
                  className={classNames('canvas', configuratorMode === 'preview' && 'canvas--preview-mode')}
                />
              </div>
              <div className={classNames('viewport__config-button-container')}>
                <Button variant="ternary" iconStart="settings" onClick={openMenu}>
                  {tTranslation('buttons.configure')}
                </Button>
              </div>
              {bootstrapper && (
                <div className={classNames('viewport__price-container')}>
                  {showPrice && (
                    <Button
                      data-price-button
                      variant="primary"
                      iconStart="shoppingBag"
                      onClick={bootstrapper.addToCart}
                    >
                      {Object.entries(bootstrapper.totalPrice).at(0)?.reverse()?.join(' ')}
                    </Button>
                  )}
                  {showRequestQuote && <RequestQuoteButton contactUsService={contactUsService!} />}
                </div>
              )}

              {bootstrapper && isCanvasLogicBrandingEnabled(configuratorUI) && (
                <div className={'viewport__branding-container'}>
                  <CanvasLogicBranding url={getCanvasLogicBrandingLink(configuratorUI)} />
                </div>
              )}
              <div className={'viewport__actions-container'}>
                {bootstrapper && !hideConfigurationControls && (
                  <Actions bootstrapper={bootstrapper} hasAr={isWebArEnabled(configuratorUI)} />
                )}
              </div>
              {!hideConfigurationControls && (
                <div className={classNames('viewport__fullscreen-container')}>
                  <Button
                    variant="ternary"
                    iconStart="expandViewport"
                    onClick={async () => {
                      if (!document.fullscreenElement && canvasRef.current) {
                        await canvasRef.current.parentElement?.parentElement?.requestFullscreen();
                        setFullscreen(true);
                      } else if (document.exitFullscreen) {
                        await document.exitFullscreen();
                        setFullscreen(false);
                      }
                    }}
                  />
                </div>
              )}
            </div>
          </div>
        )}
        {!hiddenControls && (
          <div className={classNames('viewport__controls-container')}>
            <div className={classNames('viewport__bottom-sheet-container')}>
              <SheetRouter />
            </div>
            <div className={classNames('viewport__controls-wrapper')}>
              <div className={'viewport__controls'} style={hide3d ? { width: '100%' } : {}}>
                {bootstrapper && (
                  <UiBuilder
                    configuratorUI={configuratorUI}
                    bootstrapper={bootstrapper}
                    configuration={bootstrapper.productVariant}
                  />
                )}
              </div>
            </div>
          </div>
        )}
      </main>
    </PortalProvider>
  );
}

export default observer(Basic3dLayout);
